export type ScheduleEventActionValidationContext = {
  operation: ScheduleEventActionValidationOperation
  surface: ScheduleEventActionValidationSurface
  executionStrategy: ScheduleEventActionExecutionStrategy
}

export type ScheduleEventActionIssuePresentation = {
  title: string
  problem: string
  consequence: string
  severity: ScheduleEventActionValidationIssue["severity"]
}

const defaultConsequence =
  "Kehrblatt führt die Aktion trotzdem aus und trägt die Änderung so in den Kalender ein."

const issueCopyByCode: Record<
  string,
  Pick<ScheduleEventActionIssuePresentation, "problem" | "consequence">
> = {
  time_in_past: {
    problem: "Der Eintrag beginnt in der Vergangenheit.",
    consequence: "Kehrblatt trägt ihn trotzdem genau so in den Kalender ein.",
  },
  appointment_starts_today_or_past: {
    problem:
      "Der Termin liegt heute oder früher. Für eine automatische Ankündigung ist das meistens zu knapp.",
    consequence:
      "Der Termin wird trotzdem angelegt oder geändert. Prüfen Sie selbst, ob der Kunde informiert werden muss.",
  },
  appointment_before_min_lead_days: {
    problem: "Der Termin beginnt in weniger als 24 Stunden.",
    consequence:
      "Kehrblatt trägt den Termin trotzdem ein. Prüfen Sie selbst, ob der Kunde rechtzeitig informiert werden kann.",
  },
  outside_working_hours: {
    problem: "Der Termin liegt außerhalb der hinterlegten Arbeitszeit des Mitarbeiters.",
    consequence: "Der Termin steht trotzdem im Kalender. Die Arbeitszeiten werden nicht geändert.",
  },
  overlaps_existing_event: {
    problem: "Zur gleichen Zeit steht schon ein anderer Eintrag im Kalender.",
    consequence:
      "Beide Einträge bleiben parallel bestehen. Kehrblatt verschiebt oder storniert nichts automatisch.",
  },
  soft_capacity_exceeded: {
    problem: "In diesem Flex-Zeitfenster sind schon viele flexible Termine geplant.",
    consequence:
      "Der Flextermin wird trotzdem eingetragen. Die Tour kann dadurch später enger werden.",
  },
  soft_slot_occupied: {
    problem: "In diesem Flex-Zeitfenster liegt bereits ein anderer flexibler Termin.",
    consequence:
      "Der Flextermin wird trotzdem dort eingetragen. Prüfen Sie selbst, ob beide Termine in dieses Zeitfenster passen.",
  },
  appointment_will_be_replaced: {
    problem:
      "Dieser Termin wurde bereits fachlich gebunden. Der bisherige Status bleibt nur für den alten Termin gültig.",
    consequence:
      "Kehrblatt storniert den bisherigen Termin intern und legt einen neuen unangekündigten Termin an. Es wird kein neuer Ankündigungsplan erstellt und keine automatische Kundeninfo versendet.",
  },
  employee_assignment_changed: {
    problem: "Die Mitarbeiterzuordnung des Termins ändert sich.",
    consequence:
      "Der Termin steht danach beim neu ausgewählten Mitarbeiter im Kalender. Kehrblatt ändert die Auftragsfreigaben dadurch nicht automatisch.",
  },
  employee_not_allowed_for_work_order: {
    problem:
      "Der gewählte Mitarbeiter ist im Auftrag nicht als zugelassener Mitarbeiter hinterlegt.",
    consequence:
      "Kehrblatt speichert die Zuordnung trotzdem. Prüfen Sie selbst, ob der Mitarbeiter den Auftrag übernehmen soll oder die Auftragsfreigabe angepasst werden muss.",
  },
  calendar_external_blocker_will_become_internal: {
    problem: "Dieser Blocker wurde aus einem verbundenen Kalender importiert.",
    consequence:
      "KehrBlatt übernimmt ihn als internen Blocker und entfernt den ursprünglichen Eintrag beim Kalenderanbieter. Ist für den ausgewählten Mitarbeiter ein beschreibbarer Kalender eingerichtet, wird der interne Blocker dorthin synchronisiert.",
  },
}

export const scheduleEventActionValidationTitle = ({
  operation,
  executionStrategy,
}: ScheduleEventActionValidationContext) => {
  if (executionStrategy === "CANCEL_AND_CREATE") return "Termin trotzdem ändern?"

  return operation === "CREATE" ? "Eintrag trotzdem anlegen?" : "Änderung prüfen"
}

export const scheduleEventActionValidationCancelLabel = ({
  surface,
}: ScheduleEventActionValidationContext) =>
  surface === "CALENDAR" ? "Nicht durchführen" : "Abbrechen"

export const presentScheduleEventActionIssue = (
  issue: ScheduleEventActionValidationIssue,
  _context: ScheduleEventActionValidationContext,
): ScheduleEventActionIssuePresentation => {
  const knownCopy = issueCopyByCode[issue.code]

  return {
    title:
      issue.title ||
      (issue.severity === "consequence" ? "Folge der Änderung" : "Hinweis zur Änderung"),
    problem:
      knownCopy?.problem ??
      issue.message ??
      "Kehrblatt hat einen Hinweis zu dieser Änderung gefunden.",
    consequence: knownCopy?.consequence ?? defaultConsequence,
    severity: issue.severity,
  }
}
